import { BaseConstruction } from './BaseConstruction';
import { ConstructionStep } from '../types';
import { Point } from '../objects/Point';
import { Segment } from '../objects/Segment';
import { Arc } from '../objects/Arc';
import { Angle } from '../objects/Angle';
import { DrawPoint, DrawLine, DrawArc } from '../animations/DrawPoint';
import { MoveCompass, Highlight, Fade } from '../animations/MoveCompass';
import { SVGRenderer } from '../core/SVGRenderer';

export class AngleBisectorConstruction extends BaseConstruction {
  public buildSteps(options: { x?: number; y?: number; size?: number } = {}): ConstructionStep[] {
    const startX = options.x ?? 200;
    const startY = options.y ?? 500;
    const size = options.size ?? 280;
    const r = 110;

    // Vertex O, arm OA (baseline right), arm OB (60° above baseline)
    const pO = { x: startX, y: startY };
    const pA = { x: startX + size, y: startY };
    const pB = SVGRenderer.polarToCartesian(pO, size, -Math.PI / 3); // 60° above baseline

    // Intersection points of arc1 (centered O, radius r) with both arms
    const pP1 = SVGRenderer.polarToCartesian(pO, r, 0);             // on arm OA
    const pP2 = SVGRenderer.polarToCartesian(pO, r, -Math.PI / 3);  // on arm OB

    // Arc1: centered at O, sweeps CCW from arm OA (0°) to arm OB (-60°)
    const arc1 = new Arc(this.renderer, pO, r, 0, -Math.PI / 3, true, false);

    // The true intersection of equal-radius arcs from P1 and P2:
    // P1 is at angle 0 on arm OA; intersection is at angle -PI/3 from P1
    // P2 is at angle -PI/3 on arm OB; intersection is at angle 0 from P2
    // Arc2: centered at P1, draw visible segment around -PI/3 direction (CCW: from -PI/6 to -PI/2)
    const arc2 = new Arc(this.renderer, pP1, r, -Math.PI / 6, -Math.PI / 2, true, false);

    // Arc3: centered at P2, draw visible segment around 0 direction (CCW: from PI/6 to -PI/6)
    const arc3 = new Arc(this.renderer, pP2, r, Math.PI / 6, -Math.PI / 6, true, false);

    // True P3: the actual intersection point of the two arcs
    // Computed from circle-circle intersection of (P1,r) and (P2,r)
    const pP3 = {
      x: pO.x + r * Math.cos(-Math.PI / 3) + r,
      y: pO.y + r * Math.sin(-Math.PI / 3),
    };
    const midAngle = -Math.PI / 6;
    const pBisectEnd = SVGRenderer.polarToCartesian(pO, size * 1.1, midAngle);

    const pointO = new Point(this.renderer, pO.x, pO.y, 'O');
    const pointA = new Point(this.renderer, pA.x, pA.y, 'A');
    const pointB = new Point(this.renderer, pB.x, pB.y, 'B');

    const arm1 = new Segment(this.renderer, pO, pA, false);
    const arm2 = new Segment(this.renderer, pO, pB, false);

    const pointP1 = new Point(this.renderer, pP1.x, pP1.y, 'P1');
    const pointP2 = new Point(this.renderer, pP2.x, pP2.y, 'P2');
    const pointP3 = new Point(this.renderer, pP3.x, pP3.y, 'P3');

    const bisectorRay = new Segment(this.renderer, pO, pBisectEnd, false);
    const angle1 = new Angle(this.renderer, pO, pA, pP3, '30°');
    const angle2 = new Angle(this.renderer, pO, pP3, pB, '30°');

    [pointO, pointA, pointB, pointP1, pointP2, pointP3, arm1, arm2, arc1, arc2, arc3, bisectorRay, angle1, angle2].forEach((o) =>
      o.setVisible(false)
    );

    return [
      {
        id: 'step1',
        title: '1. Given Angle AOB',
        description: 'Draw vertex O and arms OA and OB forming the given angle.',
        narration: 'Step 1: Drawing arms OA and OB of the given angle.',
        duration: 1500,
        animations: [new DrawPoint(pointO), new DrawPoint(pointA), new DrawPoint(pointB), new DrawLine(arm1), new DrawLine(arm2)],
      },
      {
        id: 'step2',
        title: '2. Draw Primary Arc Centered at Vertex O',
        description: 'Sweep compass from O to intersect both arms at points P1 and P2.',
        narration: 'Step 2: Drawing arc centered at O intersecting both arms.',
        duration: 1500,
        animations: [
          new MoveCompass(this.compass, pO, r),
          new DrawArc(arc1, this.compass),
          new DrawPoint(pointP1),
          new DrawPoint(pointP2),
        ],
      },
      {
        id: 'step3',
        title: '3. Intersect Arcs from P1 and P2',
        description: 'Draw arcs centered at P1 and P2 with equal radius to find bisecting point P3.',
        narration: 'Step 3: Drawing intersecting arcs from P1 and P2.',
        duration: 2000,
        animations: [
          new MoveCompass(this.compass, pP1, r),
          new DrawArc(arc2, this.compass),
          new MoveCompass(this.compass, pP2, r),
          new DrawArc(arc3, this.compass),
          new DrawPoint(pointP3),
        ],
      },
      {
        id: 'step4',
        title: '4. Draw Angle Bisector Ray',
        description: 'Using ruler, connect vertex O through intersection point P3.',
        narration: 'Step 4: Drawing ray from vertex O through P3 to bisect the angle.',
        duration: 1500,
        animations: [
          new Fade(this.compass, false),
          new DrawLine(bisectorRay, this.ruler),
        ],
      },
      {
        id: 'step5',
        title: '5. Highlight Equal Bisected Angles',
        description: 'Highlight two equal 30° bisected angles.',
        narration: 'Construction complete! Angle AOB is divided into two equal angles.',
        duration: 1200,
        animations: [new Fade(this.ruler, false), new Highlight(bisectorRay), new DrawPoint(angle1 as any), new DrawPoint(angle2 as any)],
      },
    ];
  }
}
